import React from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon, 
  ExclamationCircleIcon,
  CloudIcon,
  DevicePhoneMobileIcon,
  ArrowPathIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import { usePWA } from '../../contexts/PWAContext';
import { Card, H3, Body, Badge, Button } from '../ui';

interface PWAStatusProps {
  detailed?: boolean;
}

const PWAStatus: React.FC<PWAStatusProps> = ({ detailed = false }) => {
  const { 
    isSupported, 
    isInstalled, 
    isInstallable, 
    isOffline, 
    hasUpdate, 
    isLoading,
    installApp,
    updateApp,
    cacheImportantData,
    clearCache
  } = usePWA();

  const getStatusBadge = () => {
    if (isLoading) {
      return <Badge variant="default">Loading...</Badge>;
    }
    
    if (!isSupported) {
      return <Badge variant="error">Not Supported</Badge>;
    }
    
    if (isInstalled) {
      return <Badge variant="success">Installed</Badge>;
    }
    
    if (isInstallable) {
      return <Badge variant="warning">Installable</Badge>;
    }
    
    return <Badge variant="default">Available</Badge>;
  };

  const statusItems = [
    {
      label: 'PWA Support',
      value: isSupported ? 'Supported' : 'Not Supported',
      icon: isSupported ? CheckCircleIcon : ExclamationCircleIcon,
      color: isSupported ? 'text-green-600' : 'text-red-600',
      bgColor: isSupported ? 'bg-green-100 dark:bg-green-900' : 'bg-red-100 dark:bg-red-900'
    },
    {
      label: 'Installation',
      value: isInstalled ? 'Installed' : 'Web Version',
      icon: isInstalled ? DevicePhoneMobileIcon : CloudIcon,
      color: isInstalled ? 'text-green-600' : 'text-gray-600',
      bgColor: isInstalled ? 'bg-green-100 dark:bg-green-900' : 'bg-gray-100 dark:bg-gray-800'
    },
    {
      label: 'Connection',
      value: isOffline ? 'Offline' : 'Online',
      icon: CloudIcon,
      color: isOffline ? 'text-yellow-600' : 'text-green-600',
      bgColor: isOffline ? 'bg-yellow-100 dark:bg-yellow-900' : 'bg-green-100 dark:bg-green-900'
    },
    {
      label: 'Updates',
      value: hasUpdate ? 'Available' : 'Up to Date',
      icon: ArrowPathIcon,
      color: hasUpdate ? 'text-blue-600' : 'text-green-600',
      bgColor: hasUpdate ? 'bg-blue-100 dark:bg-blue-900' : 'bg-green-100 dark:bg-green-900'
    }
  ];

  if (!detailed) {
    return (
      <div className="flex items-center space-x-2">
        <div className="flex items-center space-x-1">
          <CubeIcon className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600 dark:text-gray-400">PWA:</span>
        </div>
        {getStatusBadge()}
      </div>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <H3 className="flex items-center space-x-2">
          <CubeIcon className="w-5 h-5 text-primary-600" />
          <span>PWA Status</span>
        </H3>
        {getStatusBadge()}
      </div>

      <div className="space-y-4">
        {statusItems.map((item, index) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
          >
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${item.bgColor}`}>
                <item.icon className={`w-5 h-5 ${item.color}`} />
              </div>
              <div>
                <Body className="font-medium">{item.label}</Body>
                <Body className="text-sm text-gray-600 dark:text-gray-400">
                  {item.value}
                </Body>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Action buttons */}
      <div className="mt-6 space-y-3">
        {isInstallable && (
          <Button
            variant="primary"
            onClick={installApp}
            className="w-full flex items-center justify-center space-x-2"
          >
            <DevicePhoneMobileIcon className="w-4 h-4" />
            <span>Install App</span>
          </Button>
        )}
        
        {hasUpdate && (
          <Button
            variant="secondary"
            onClick={updateApp}
            className="w-full flex items-center justify-center space-x-2"
          >
            <ArrowPathIcon className="w-4 h-4" />
            <span>Update App</span>
          </Button>
        )}
        
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={cacheImportantData}
            className="flex-1 flex items-center justify-center space-x-2"
          >
            <CloudIcon className="w-4 h-4" />
            <span>Cache Data</span>
          </Button>
          
          <Button
            variant="outline"
            onClick={clearCache}
            className="flex-1"
          >
            Clear Cache
          </Button>
        </div>
      </div>

      {/* Info section */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <Body className="text-sm text-blue-800 dark:text-blue-200">
          <strong>PWA Benefits:</strong> Install for offline access, faster loading, 
          and native app-like experience on mobile and desktop.
        </Body>
      </div>
    </Card>
  );
};

export default PWAStatus;
